import { RefreshCw } from "lucide-react";
import { Fragment } from "react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";

import type { PayoutBatchWarning } from "../../types/payoutBatches.types";

export interface BatchFeesUpdatedAlertProps {
  warning: PayoutBatchWarning;
}

interface AmendedLine {
  key: string;
  label: string;
  before: string;
  after: string;
  currency: string;
}

/**
 * The batch was repriced because the fee configuration changed: the selection's fees and debit as they were,
 * struck through, next to what they are now — read like an amended invoice line before confirming.
 */
export function BatchFeesUpdatedAlert({ warning }: BatchFeesUpdatedAlertProps) {
  const { t } = useTranslations("payoutBatches");

  const candidates = [
    {
      key: "fees",
      label: t("warning_fees_updated_fees"),
      before: warning.previous_fees_selected,
      after: warning.fees_selected,
      currency: warning.fees_currency,
    },
    {
      key: "debit",
      label: t("warning_fees_updated_debit"),
      before: warning.previous_debit_selected,
      after: warning.debit_selected,
      currency: warning.debit_currency,
    },
  ];
  const lines = candidates.filter(
    (line): line is AmendedLine => !!line.before && !!line.after && !!line.currency,
  );

  return (
    <ColocaAlert
      variant="warning"
      icon={RefreshCw}
      heading={t("warning_fees_updated_title")}
      text={
        <div className="space-y-3">
          <p>{t("warning_fees_updated_text")}</p>
          {lines.length > 0 && (
            <div className="grid grid-cols-[auto_auto_auto] items-baseline justify-start gap-x-6 gap-y-1.5">
              <span aria-hidden="true" />
              <span
                aria-hidden="true"
                className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase"
              >
                {t("warning_fees_updated_before")}
              </span>
              <span
                aria-hidden="true"
                className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase"
              >
                {t("warning_fees_updated_after")}
              </span>
              {lines.map((line) => (
                <Fragment key={line.key}>
                  <span className="text-sm text-foreground">{line.label}</span>
                  <span>
                    <span className="sr-only">{t("warning_fees_updated_before")}: </span>
                    <ColocaAmountDisplay
                      value={line.before}
                      currency={line.currency}
                      fractionDigits={minorUnitsFor(line.currency)}
                      size="xs"
                      className="text-muted-foreground line-through"
                    />
                  </span>
                  <span>
                    <span className="sr-only">{t("warning_fees_updated_after")}: </span>
                    <ColocaAmountDisplay
                      value={line.after}
                      currency={line.currency}
                      fractionDigits={minorUnitsFor(line.currency)}
                      size="sm"
                    />
                  </span>
                </Fragment>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}
